import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchAuthSession, getCurrentUser, signIn as amplifySignIn, signOut as amplifySignOut, confirmSignUp } from 'aws-amplify/auth';
import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocumentClient, ScanCommand } from "@aws-sdk/lib-dynamodb";
import awsmobile from '../aws-exports.js';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true); // Start with loading true

    // Obtain AWS credentials from the current Amplify session
    const getAwsCredentials = useCallback(async () => {
        try {
            // forceRefresh to ensure we pick up fresh identity credentials after sign-in
            const { credentials } = await fetchAuthSession({ forceRefresh: true });
            if (credentials?.accessKeyId && credentials?.secretAccessKey) {
                return {
                    accessKeyId: credentials.accessKeyId,
                    secretAccessKey: credentials.secretAccessKey,
                    sessionToken: credentials.sessionToken,
                };
            }
            console.error('❌ Missing AWS credentials from session');
        } catch (error) {
            console.error('❌ Unable to obtain AWS credentials:', error.message);
        }
        return null;
    }, []);

    // Add a new function to fetch company information
    const fetchCompanyInfo = useCallback(async (companyId, shouldLog = false) => {
        try {
            const sessionCredentials = await getAwsCredentials();
            if (!sessionCredentials) {
                throw new Error('Missing AWS credentials');
            }

            const client = new DynamoDBClient({
                region: awsmobile.aws_project_region,
                credentials: sessionCredentials,
            });
            const docClient = DynamoDBDocumentClient.from(client);

            const command = new ScanCommand({
                TableName: awsmobile.dynamodb_users_table,
                FilterExpression: "PK = :pk AND SK = :sk AND itemType = :itemType",
                ExpressionAttributeValues: {
                    ":pk": `COMPANY#${companyId}`,
                    ":sk": "METADATA",
                    ":itemType": "Company"
                }
            });

            const response = await docClient.send(command);

            if (response.Items && response.Items.length > 0) {
                const companyInfo = response.Items[0];

                // Only log if shouldLog is true
                if (shouldLog) {
                    console.log('\n🏢 ===================== COMPANY INFORMATION =====================');
                    console.log(`📋 Company ID: ${companyInfo.companyId}`);
                    console.log(`🏢 Company Name: ${companyInfo.companyName}`);
                    console.log(`📊 Company Status: ${companyInfo.companyStatus}`);
                    console.log(`📅 Created At: ${companyInfo.createdAt}`);
                    console.log('🏢 ============================================================\n');
                }

                return companyInfo;
            }
        } catch (error) {
            console.error('❌ Error fetching company info:', error.message);
        }

        return null;
    }, [getAwsCredentials]);

    // Fetch user profile function
    const fetchUserProfile = useCallback(async (email, shouldLog = false) => {
        try {
            const sessionCredentials = await getAwsCredentials();
            if (!sessionCredentials) {
                throw new Error('Missing AWS credentials');
            }

            const client = new DynamoDBClient({
                region: awsmobile.aws_project_region,
                credentials: sessionCredentials,
            });
            const docClient = DynamoDBDocumentClient.from(client);

            const command = new ScanCommand({
                TableName: awsmobile.dynamodb_users_table,
                FilterExpression: "email = :email AND itemType = :itemType",
                ExpressionAttributeValues: {
                    ":email": email,
                    ":itemType": "User"
                }
            });

            const response = await docClient.send(command);

            if (response.Items && response.Items.length > 0) {
                const userProfile = response.Items[0];

                // Only log if shouldLog is true
                if (shouldLog) {
                    console.log('\n👤 ======================== USER INFORMATION ========================');
                    console.log(`📧 Email: ${userProfile.email}`);
                    console.log(`👤 Full Name: ${userProfile.fullName}`);
                    console.log(`🆔 User ID: ${userProfile.userId}`);
                    console.log(`📝 Username: ${userProfile.username}`);
                    console.log(`🔐 Approval Status: ${userProfile.userApprovalStatus}`);
                    console.log(`🆕 First Login: ${userProfile.isFirstLogin}`);
                    console.log('👤 ================================================================\n');

                    // Extract company ID and fetch company info
                    const companyId = userProfile.PK?.replace('COMPANY#', '');
                    if (companyId) {
                        await fetchCompanyInfo(companyId, true); // Log company info too
                    }
                }

                // Store in state and localStorage (silently)
                setUserProfile(userProfile);
                localStorage.setItem('userProfile', JSON.stringify(userProfile));

                return userProfile;
            }
        } catch (error) {
            console.error('❌ Error fetching user profile:', error.message);
            setUserProfile(null);
        }

        return null;
    }, [getAwsCredentials, fetchCompanyInfo]);

    // Check authentication function
    const checkAuth = useCallback(async () => {
        try {
            const currentUser = await getCurrentUser();
            if (!currentUser) {
                setUser(null);
                setUserProfile(null);
                setIsAuthenticated(false);
                setIsAdmin(false);
                localStorage.removeItem('userProfile');
                return;
            }

            setUser(currentUser);
            setIsAuthenticated(true);

            const email = currentUser.signInDetails?.loginId || currentUser.username;
            if (email) {
                const profile = await fetchUserProfile(email, false);
                if (profile) {
                    setUserProfile(profile);
                    if (email === 'admin@valuepay.ai') setIsAdmin(true);
                }
            }
        } catch (error) {
            const unauthenticated =
                error?.message?.toLowerCase().includes('needs to be authenticated') ||
                error?.message?.toLowerCase().includes('not authenticated') ||
                error?.name === 'UserUnAuthenticatedException';
            if (!unauthenticated) {
                console.error('❌ Auth check failed:', error.message);
            }
            setUser(null);
            setUserProfile(null);
            setIsAuthenticated(false);
            setIsAdmin(false);
            localStorage.removeItem('userProfile');
        } finally {
            setLoading(false);
        }
    }, [fetchUserProfile]);

    // Check localStorage on component mount for stored profile
    useEffect(() => {
        const storedProfile = localStorage.getItem('userProfile');
        if (storedProfile) {
            try {
                setUserProfile(JSON.parse(storedProfile));
            } catch (e) {
                console.error('Failed to parse stored profile', e);
                localStorage.removeItem('userProfile');
            }
        }
        // Check authentication status on mount
        checkAuth();
    }, [checkAuth]);

    // Sign in function
    const signIn = async (email, password) => {
        try {
            setLoading(true);

            // Check if user is already signed in
            try {
                const existingUser = await getCurrentUser();
                if (existingUser) {
                    // Sign out the existing user first
                    await amplifySignOut();
                    setUser(null);
                    setUserProfile(null);
                    setIsAuthenticated(false);
                    setIsAdmin(false);
                    localStorage.removeItem('userProfile');
                }
            } catch (e) {
                // No user signed in, continue
            }

            const result = await amplifySignIn({
                username: email,
                password: password,
            });

            if (result.isSignedIn) {
                // Clear console before showing new user information
                console.clear();

                const currentUser = await getCurrentUser();
                setUser(currentUser);
                setIsAuthenticated(true);

                await fetchUserProfile(email, true);

                if (email === 'admin@valuepay.ai') {
                    setIsAdmin(true);
                    return { success: true, isAdmin: true };
                }

                return { success: true };
            }

            // This case is for handling next steps like CONFIRM_SIGN_UP
            return { success: false, nextStep: result.nextStep.signInStep };

        } catch (error) {
            if (error.name === 'UserNotConfirmedException') {
                // Suppress browser network error log for this expected flow
                console.clear();
                return { success: false, nextStep: 'CONFIRM_SIGN_UP' };
            }
            if (error.message && error.message.includes('already a signed in user')) {
                // This shouldn't happen now, but just in case
                try {
                    await amplifySignOut();
                    // Retry the sign in
                    return await signIn(email, password);
                } catch (retryError) {
                    console.error('❌ Retry sign in error:', retryError.message);
                    return { success: false, error: retryError.message };
                }
            }
            console.error('❌ Sign in error:', error.message);
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    // Sign out function
    const signOut = async (navigate) => {
        try {
            // Clear console on sign out for a clean slate
            console.clear();
            await amplifySignOut();
            setUser(null);
            setUserProfile(null);
            setIsAuthenticated(false);
            setIsAdmin(false);
            localStorage.removeItem('userProfile');
            console.log('✅ User signed out successfully - Console cleared for next user');
            if (navigate) {
                navigate('/');
            }
        } catch (error) {
            console.error('❌ Error signing out:', error.message);
        }
    };



    // Confirm sign up function
    const handleConfirmSignUp = async (username, confirmationCode) => {
        try {
            await confirmSignUp({
                username,
                confirmationCode,
            });
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const contextValue = {
        user,
        userProfile,
        isAuthenticated,
        isAdmin,
        loading,
        signIn,
        signOut,
        checkAuth,
        fetchUserProfile,
        fetchCompanyInfo,
        handleConfirmSignUp,
    };

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};